import { createContext, useContext, useMemo, type ReactNode, type RefObject } from 'react';
import { GlobeDirector } from '../globe/director';
import { useSectionMetrics, type SectionMetrics } from '../motion/scroll';

/**
 * One persistent globe behind normally scrolling content. Sections that show it (hero, journey)
 * claim it while they cross the viewport; every other section is opaque and the engine pauses.
 */
export interface StageApi { globe: GlobeDirector; show(owner: string, visible: boolean): void }
const StageContext = createContext<StageApi | null>(null);

export function StageProvider({ children }: { children: ReactNode }) {
  const api = useMemo<StageApi>(() => {
    const globe = new GlobeDirector(); const owners = new Set<string>();
    return {
      globe,
      show(owner, visible) {
        const had = owners.size > 0; if (visible) owners.add(owner); else owners.delete(owner);
        const has = owners.size > 0; if (has === had) return;
        document.documentElement.dataset.globe = has ? 'on' : 'off'; globe.setActive(has);
      },
    };
  }, []);
  return <StageContext.Provider value={api}>{children}</StageContext.Provider>;
}

export function useStage() {
  const api = useContext(StageContext); if (!api) throw new Error('STAGE_PROVIDER_MISSING');
  return api;
}

/** A section that shows the globe while visible and receives per-frame metrics. */
export function useGlobeSection(ref: RefObject<Element | null>, owner: string, onMetrics?: (metrics: SectionMetrics, stage: StageApi) => void) {
  const stage = useStage();
  useSectionMetrics(ref, metrics => {
    stage.show(owner, metrics.visible);
    if (metrics.visible) onMetrics?.(metrics, stage);
  });
}
